import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Home, 
  CheckSquare, 
  Calendar, 
  ShoppingCart, 
  Users, 
  Package,
  MapPin,
  Heart,
  DollarSign,
  Settings,
  BookOpen,
  ChevronRight,
  ChevronDown,
  Video,
  MessageSquare,
  BarChart3
} from 'lucide-react'

const UserGuide = () => {
  const [expandedSection, setExpandedSection] = useState('dashboard')

  const sections = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: Home,
      description: 'Painel principal com visão geral de tudo',
      content: [
        {
          subtitle: 'Estatísticas Rápidas',
          items: [
            'Visualize gastos do mês atual',
            'Acompanhe tarefas pendentes e concluídas',
            'Veja eventos do dia',
            'Monitore lista de compras'
          ]
        },
        {
          subtitle: 'Ações Rápidas',
          items: [
            'Clique nos cards para navegar rapidamente',
            'Use os botões de ação rápida para criar itens',
            'Acesse o status da família no painel'
          ]
        },
        {
          subtitle: 'Insights Inteligentes',
          items: [
            'Receba recomendações personalizadas',
            'Veja dicas para melhorar a organização',
            'Acompanhe o bem-estar da família'
          ]
        }
      ]
    },
    {
      id: 'tasks',
      title: 'Tarefas',
      icon: CheckSquare,
      description: 'Gerencie tarefas e responsabilidades da família',
      content: [
        {
          subtitle: 'Criar Tarefa',
          items: [
            'Clique no botão "Nova Tarefa"',
            'Preencha título, descrição e data de vencimento',
            'Defina prioridade (Baixa, Média, Alta)',
            'Escolha dificuldade e pontos',
            'Atribua para membros da família'
          ]
        },
        {
          subtitle: 'Filtrar e Buscar',
          items: [
            'Use os filtros: Todas, Pendentes, Concluídas',
            'Digite na busca para encontrar tarefas específicas',
            'Visualize estatísticas por membro da família'
          ]
        },
        {
          subtitle: 'Gerenciar Tarefas',
          items: [
            'Clique na tarefa para ver detalhes',
            'Marque como concluída quando finalizada',
            'Edite ou exclua tarefas quando necessário',
            'Acompanhe pontos ganhos por membro'
          ]
        }
      ]
    },
    {
      id: 'calendar',
      title: 'Calendário',
      icon: Calendar,
      description: 'Organize eventos e compromissos familiares',
      content: [
        {
          subtitle: 'Visualizar Calendário',
          items: [
            'Navegue entre meses usando as setas',
            'Clique em "Hoje" para voltar ao mês atual',
            'Veja eventos marcados nos dias',
            'Eventos aparecem destacados nos dias'
          ]
        },
        {
          subtitle: 'Criar Evento',
          items: [
            'Clique no botão "Novo Evento"',
            'Ou clique diretamente em um dia do calendário',
            'Preencha título, data, horário e local',
            'Adicione descrição e convidados',
            'Salve o evento'
          ]
        },
        {
          subtitle: 'Gerenciar Eventos',
          items: [
            'Visualize eventos próximos na lista abaixo',
            'Clique em um evento para ver detalhes',
            'Edite ou exclua eventos quando necessário',
            'Receba lembretes antes dos eventos'
          ]
        }
      ]
    },
    {
      id: 'shopping',
      title: 'Lista de Compras',
      icon: ShoppingCart,
      description: 'Organize suas compras com sugestões inteligentes',
      content: [
        {
          subtitle: 'Criar Lista',
          items: [
            'Clique em "Nova Lista"',
            'Dê um nome para sua lista (ex: "Supermercado", "Farmácia")',
            'Adicione descrição opcional',
            'Comece adicionando itens'
          ]
        },
        {
          subtitle: 'Adicionar Itens',
          items: [
            'Preencha nome do item',
            'Defina quantidade necessária',
            'Escolha categoria (alimentação, limpeza, etc.)',
            'Defina prioridade (Alta, Média, Baixa)',
            'Salve o item'
          ]
        },
        {
          subtitle: 'Gerenciar Lista',
          items: [
            'Marque itens como comprados quando adquirir',
            'Use filtros: Todos, Pendentes, Concluídos',
            'Busque itens específicos na lista',
            'Veja estatísticas: total, pendentes, concluídos',
            'Use sugestões inteligentes para itens comuns'
          ]
        }
      ]
    },
    {
      id: 'family-board',
      title: 'Mural da Família',
      icon: Users,
      description: 'Compartilhe momentos e bem-estar da família',
      content: [
        {
          subtitle: 'Criar Postagem',
          items: [
            'Clique em "Nova Postagem"',
            'Escreva sua mensagem',
            'Adicione foto ou mídia (opcional)',
            'Escolha tipo: momento, anúncio, lembrete',
            'Publique para toda a família ver'
          ]
        },
        {
          subtitle: 'Interagir',
          items: [
            'Veja postagens de todos os membros',
            'Curtir postagens',
            'Comentar em postagens',
            'Visualizar insights de bem-estar da família'
          ]
        },
        {
          subtitle: 'Bem-estar',
          items: [
            'Veja o humor geral da família',
            'Receba recomendações personalizadas',
            'Acompanhe o status de todos os membros',
            'Visualize indicadores de bem-estar'
          ]
        }
      ]
    },
    {
      id: 'items-control',
      title: 'Controle de Itens',
      icon: Package,
      description: 'Gerencie manutenção e controle de itens da família',
      content: [
        {
          subtitle: 'Adicionar Item',
          items: [
            'Clique em "Novo Item"',
            'Informe nome e descrição',
            'Selecione categoria (Eletrodoméstico, Móvel, etc.)',
            'Adicione data de compra e valor',
            'Defina intervalo de manutenção',
            'Informe localização do item'
          ]
        },
        {
          subtitle: 'Manutenção',
          items: [
            'Veja itens que precisam de manutenção',
            'Registre quando fez manutenção',
            'Defina quando fazer próxima manutenção',
            'Registre custos de manutenção',
            'Receba alertas de manutenção vencida'
          ]
        },
        {
          subtitle: 'Gerenciar',
          items: [
            'Visualize todos os itens da família',
            'Filtre: Todos, Precisam Manutenção, Manutenção Recente',
            'Busque itens por nome ou categoria',
            'Veja histórico de manutenções',
            'Acompanhe gastos com manutenção'
          ]
        }
      ]
    },
    {
      id: 'locator',
      title: 'Localizador',
      icon: MapPin,
      description: 'Visualize localização de membros da família',
      content: [
        {
          subtitle: 'Ver Sua Localização',
          items: [
            'Clique em "Atualizar posição"',
            'Permita acesso à localização quando solicitado',
            'Sua posição aparecerá no mapa',
            'Veja precisão da localização'
          ]
        },
        {
          subtitle: 'Acompanhamento',
          items: [
            'Use "Iniciar acompanhamento" para atualização automática',
            'Veja todos os membros da família no mapa',
            'Visualize última atualização de cada membro',
            'Status online/offline dos membros'
          ]
        },
        {
          subtitle: 'Mapa',
          items: [
            'Zoom in/out para ver mais detalhes',
            'Clique nos marcadores para ver informações',
            'Navegue pelo mapa arrastando',
            'Veja precisão da localização indicada'
          ]
        }
      ]
    },
    {
      id: 'finance',
      title: 'Finanças',
      icon: DollarSign,
      description: 'Controle de gastos e orçamento familiar',
      content: [
        {
          subtitle: 'Registrar Transação',
          items: [
            'Clique em "Nova Transação"',
            'Escolha tipo: Receita ou Despesa',
            'Informe valor e categoria',
            'Adicione descrição',
            'Selecione data da transação',
            'Anexe comprovante (opcional)'
          ]
        },
        {
          subtitle: 'Visualizar Relatórios',
          items: [
            'Veja gráficos de gastos por categoria',
            'Acompanhe receitas e despesas mensais',
            'Visualize tendências e histórico',
            'Compare períodos diferentes'
          ]
        },
        {
          subtitle: 'Orçamento',
          items: [
            'Defina limites de gastos por categoria',
            'Receba alertas quando próximo do limite',
            'Acompanhe percentual usado',
            'Visualize relatórios mensais'
          ]
        }
      ]
    },
    {
      id: 'settings',
      title: 'Configurações',
      icon: Settings,
      description: 'Personalize sua experiência',
      content: [
        {
          subtitle: 'Notificações',
          items: [
            'Ative/desative notificações push',
            'Configure notificações por categoria',
            'Defina horários de não perturbe',
            'Escolha sons e vibração'
          ]
        },
        {
          subtitle: 'Privacidade',
          items: [
            'Configure quem pode ver suas informações',
            'Gerencie compartilhamento de localização',
            'Ajuste visibilidade de posts'
          ]
        },
        {
          subtitle: 'Família',
          items: [
            'Gerencie relacionamentos familiares',
            'Defina papéis e permissões',
            'Adicione ou remova membros'
          ]
        }
      ]
    },
    {
      id: 'video-room',
      title: 'Sala de Vídeo',
      icon: Video,
      description: 'Chamadas de vídeo em família',
      content: [
        {
          subtitle: 'Iniciar Chamada',
          items: [
            'Clique em "Iniciar chamada"',
            'Permita acesso à câmera e microfone',
            'Aguarde conexão',
            'Veja participantes da família'
          ]
        },
        {
          subtitle: 'Controles',
          items: [
            'Ligue/desligue vídeo com botão Vídeo',
            'Ligue/desligue áudio com botão Áudio',
            'Ajuste qualidade do vídeo nas configurações',
            'Encerre chamada quando terminar'
          ]
        }
      ]
    },
    {
      id: 'chat',
      title: 'Chat',
      icon: MessageSquare,
      description: 'Conversas em tempo real',
      content: [
        {
          subtitle: 'Enviar Mensagem',
          items: [
            'Digite sua mensagem no campo',
            'Clique em enviar ou pressione Enter',
            'Compartilhe localização quando necessário',
            'Use anexos para enviar arquivos'
          ]
        },
        {
          subtitle: 'Funcionalidades',
          items: [
            'Veja histórico de mensagens',
            'Compartilhe localização em tempo real',
            'Inicie chamada de vídeo direto do chat',
            'Receba notificações de novas mensagens'
          ]
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics',
      icon: BarChart3,
      description: 'Análises e métricas da família',
      content: [
        {
          subtitle: 'Métricas',
          items: [
            'Veja total de usuários ativos',
            'Acompanhe sessões em tempo real',
            'Monitore alertas SOS',
            'Visualize atualizações de localização'
          ]
        },
        {
          subtitle: 'Eventos e Erros',
          items: [
            'Veja eventos recentes do sistema',
            'Monitore erros e problemas',
            'Analise tendências de uso',
            'Identifique problemas de performance'
          ]
        }
      ]
    }
  ]

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-8 text-white">
        <div className="flex items-center space-x-4 mb-4">
          <div className="p-3 bg-white/20 rounded-lg">
            <BookOpen className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Manual do Usuário</h1>
            <p className="text-blue-100 mt-1">
              Guia completo de como usar cada funcionalidade do Domus Family Hub
            </p>
          </div>
        </div>
      </div>

      {/* Seções */}
      <div className="space-y-4">
        {sections.map((section, index) => {
          const Icon = section.icon
          const isExpanded = expandedSection === section.id

          return (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden"
            >
              {/* Header da Seção */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-6 hover:bg-neutral-50 transition-colors text-left"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-neutral-900">
                      {section.title}
                    </h2>
                    <p className="text-sm text-neutral-600 mt-1">
                      {section.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded">
                    {section.content.reduce((acc, c) => acc + c.items.length, 0)} dicas
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-neutral-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-neutral-400" />
                  )}
                </div>
              </button>

              {/* Conteúdo Expandido */}
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-neutral-200"
                >
                  <div className="p-6 space-y-6">
                    {section.content.map((content, contentIndex) => (
                      <div key={contentIndex}>
                        <h3 className="text-lg font-semibold text-neutral-900 mb-3 flex items-center">
                          <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                          {content.subtitle}
                        </h3>
                        <ul className="space-y-2 ml-5">
                          {content.items.map((item, itemIndex) => (
                            <li
                              key={itemIndex}
                              className="text-neutral-700 flex items-start"
                            >
                              <span className="text-blue-500 mr-2 mt-1">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )
        })}
      </div>

      {/* Dicas Rápidas */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: sections.length * 0.05 }}
        className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200 p-6"
      >
        <h2 className="text-xl font-semibold text-neutral-900 mb-4 flex items-center">
          <Heart className="h-5 w-5 text-green-600 mr-2" />
          Dicas Rápidas
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4">
            <h3 className="font-medium text-neutral-900 mb-2">💡 Dica</h3>
            <p className="text-sm text-neutral-600">
              Use o Dashboard para ter uma visão geral rápida de tudo que está acontecendo na família.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h3 className="font-medium text-neutral-900 mb-2">⚡ Dica</h3>
            <p className="text-sm text-neutral-600">
              Ative as notificações para receber lembretes importantes sobre tarefas e eventos.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h3 className="font-medium text-neutral-900 mb-2">🎯 Dica</h3>
            <p className="text-sm text-neutral-600">
              Compartilhe momentos no Mural da Família para manter todos conectados.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h3 className="font-medium text-neutral-900 mb-2">🔔 Dica</h3>
            <p className="text-sm text-neutral-600">
              Configure o Localizador para saber onde estão os membros da família em tempo real.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Suporte */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: (sections.length + 1) * 0.05 }}
        className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 text-center"
      >
        <h2 className="text-xl font-semibold text-neutral-900 mb-2">
          Precisa de Ajuda?
        </h2>
        <p className="text-neutral-600 mb-4">
          Se você tiver dúvidas ou encontrar algum problema, não hesite em entrar em contato.
        </p>
        <div className="flex items-center justify-center space-x-4">
          <div className="text-sm text-neutral-500">
            📧 Email: suporte@domusfamilyhub.com
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default UserGuide

